import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import wootConstants from 'dashboard/constants/globals';
import { flushAllIndexedDB } from '../helper/CacheHelper/flushIndexedDB';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import whatsappCallSession from './whatsappCallSession';

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'first.reply.created': this.onFirstReplyCreated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'agent.update': this.onUpdateAgent,
      'report.download': this.onReportDownload,
      'whatsapp.call.updated': this.onWhatsappCallUpdated,
      'whatsapp.call.ended': this.onWhatsappCallEnded,
      'voice_call.outbound_connected': this.onVoiceCallUpdated,
      'voice_call.outbound_accepted': this.onVoiceCallOutboundAccepted,
      'voice_call.accepted': this.onVoiceCallAccepted,
      'voice_call.ended': this.onVoiceCallEnded,
      'voice_call.incoming_created': this.onVoiceCallIncoming,
      'voice_call.permission_granted': this.onVoiceCallPermissionGranted,
    };
  }

  onReconnect = () => {
    this.syncActiveConversationMessages();
  };

  onWhatsappCallUpdated = data => {
    bus.$emit('whatsapp-call-updated', data);
    this.app.$store.dispatch('calls/updateCallStatus', {
      callSid: data.call_id,
      status: data.status,
    });
  };

  onWhatsappCallEnded = data => {
    this.app.$store.dispatch('calls/updateCallStatus', {
      callSid: data.call_id,
      status: data.status,
      duration: data.duration_seconds,
      endReason: data.end_reason,
    });
    this.app.$store.dispatch('calls/removeCall', data.call_id);
    if (whatsappCallSession.isActiveCall(data.call_id)) {
      whatsappCallSession.cleanup({ callSid: data.call_id });
    }
  };

  onVoiceCallUpdated = data => {
    if (!whatsappCallSession.isActiveCall(data.call_id)) return;
    this.app.$store.dispatch('calls/updateCallStatus', {
      callSid: data.call_id,
      status: data.status,
      duration: data.duration_seconds,
      sdpAnswer: data.sdp_answer,
    });
    if (data.sdp_answer) whatsappCallSession.applySdpAnswer(data.sdp_answer);
  };

  onVoiceCallOutboundAccepted = data => {
    this.onVoiceCallUpdated(data);
  };

  onVoiceCallAccepted = data => {
    const currentUserId = this.app.$store.getters.getCurrentUserID;
    if (
      data.accepted_by_agent_id &&
      data.accepted_by_agent_id !== currentUserId
    ) {
      this.app.$store.dispatch('calls/removeCall', data.call_id);
      return;
    }
    // Mesmo agente atendeu em outra aba: esta aba não detém a sessão WebRTC
    // da chamada, então remove o card de "recebendo" e para o toque.
    if (whatsappCallSession.activeCallSid !== data.call_id) {
      this.app.$store.dispatch('calls/removeCall', data.call_id);
      return;
    }
    this.app.$store.dispatch('calls/updateCallStatus', {
      callSid: data.call_id,
      status: data.status,
      duration: data.duration_seconds,
      sdpAnswer: data.sdp_answer,
    });
    if (data.sdp_answer && whatsappCallSession.isActiveCall(data.call_id)) {
      whatsappCallSession.applySdpAnswer(data.sdp_answer);
    }
  };

  onVoiceCallEnded = data => {
    this.app.$store.dispatch('calls/updateCallStatus', {
      callSid: data.call_id,
      status: data.status,
      duration: data.duration_seconds,
    });
    this.app.$store.dispatch('calls/removeCall', data.call_id);
    if (whatsappCallSession.isActiveCall(data.call_id)) {
      this.app.$store.dispatch('calls/clearActiveCall');
      whatsappCallSession.cleanup({ callSid: data.call_id });
    }
  };

  onVoiceCallIncoming = data => {
    this.app.$store.dispatch('calls/addCall', {
      accountId: data.account_id,
      callSid: data.call_id,
      callId: data.id,
      provider: 'whatsapp',
      status: 'ringing',
      callDirection: 'inbound',
      conversationId: data.conversation_id,
      inboxId: data.inbox_id,
      caller: data.caller,
      sdpOffer: data.sdp_offer,
      iceServers: data.ice_servers,
      recordingEnabled: data.recording_enabled,
    });
  };

  onVoiceCallPermissionGranted = data => {
    bus.$emit('whatsapp-call-permission-granted', data);
  };

  onReportDownload = data => {
    const { TYPE_CSV } = wootConstants;
    const report = data?.report?.replace(/^"+|"+$/g, '');
    const content = report.replace(/\\n/g, '\n');
    const fileName = data?.filename;

    const blob = new Blob([content], { type: TYPE_CSV });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.setAttribute('download', fileName);
    link.setAttribute('href', url);
    link.click();
    URL.revokeObjectURL(url);
  };

  onUpdateAgent = data => {
    if (this.app.$store.getters.getCurrentUserID === data.agent_id) {
      this.app.$store.dispatch('setUser', data.agent);
    }
  };

  onSummaryRead = data => {
    this.app.$store.dispatch('setAccountSummary', data);
  };

  onReportRead = data => {
    this.app.$store.dispatch('setAccountReport', data);
  };

  onDisconnected = () => {
    this.setActiveConversationLastMessageId();
  };

  setActiveConversationLastMessageId = () => {
    const {
      params: { conversation_id: conversationId },
    } = this.app.$route;
    if (conversationId) {
      this.app.$store.dispatch('setConversationLastMessageId', {
        conversationId: Number(conversationId),
      });
    }
  };

  syncActiveConversationMessages = () => {
    const {
      params: { conversation_id: conversationId },
    } = this.app.$route;
    if (conversationId) {
      this.app.$store.dispatch('syncActiveConversationMessages', {
        conversationId: Number(conversationId),
      });
    }
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
    if (data.message_type === MESSAGE_TYPE.INCOMING) {
      window.bus.$emit(BUS_EVENTS.INCOMING_MESSAGE_RECEIVED, {
        conversationId,
        contactId: data.sender?.id,
        message: data,
      });
    }
  };

  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  fetchConversationStats = () => {
    bus.$emit('fetch_conversation_stats');
    bus.$emit('fetch_overview_reports');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onFirstReplyCreated = () => {
    bus.$emit('fetch_overview_reports');
  };

  onCacheInvalidate = async data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('labelsCrm/revalidate', {
      newKey: keys.label_crm,
    });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });

    await flushAllIndexedDB();
  };
}

export default {
  init(pubsubToken) {
    return new ActionCableConnector(window.WOOT, pubsubToken);
  },
};
